/**
 *
 * AuthGuard
 *
 */
import { useAuth0 } from '@auth0/auth0-react'
import PropTypes from 'prop-types'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { logout } from '~/lib/actions'
import { setAuthGetter } from '~/lib/auth'
import { setAuth } from '~/store/auth'
import { selectAuth } from '~/store/auth/selectors'
import { checkUserChanged } from '~/store/persistUserId'
import Loader from '../Loader'

const logger = console.scope('AuthGuard')

const AuthWrapper = ({ children }) => {
	const { getAccessTokenSilently, user, loginWithRedirect } = useAuth0()
	const dispatch = useDispatch()
	const { token } = useSelector(selectAuth)

	// Store the getter function so RTK Query can get fresh tokens
	useEffect(() => {
		setAuthGetter(getAccessTokenSilently)
	}, [getAccessTokenSilently])

	// Fetch initial token and user info on mount (populates Redux for UI state)
	useEffect(() => {
		const initializeAuth = async () => {
			try {
				const accessToken = await getAccessTokenSilently()

				// Check if a different user logged in - if so, reload to get correct persist keys
				if (user?.sub && checkUserChanged(user.sub)) {
					logger.info('User changed, reloading to apply user-specific settings...')
					window.location.reload()
					return
				}

				dispatch(setAuth({ token: accessToken, user }))
			} catch (error) {
				logger.error('Error getting token:', error)
				if (error.message && error.message.includes('WRONG_ENV_FOR_USER')) {
					logger.warn('Wrong environment detected, logging out...')
					logout()
				} else {
					logger.warn('Token refresh failed, redirecting to login...')
					loginWithRedirect({ appState: { returnTo: window.location.pathname } })
				}
			}
		}

		if (!token) {
			initializeAuth()
		}
	}, [getAccessTokenSilently, dispatch, user, token, loginWithRedirect])

	if (!token) {
		return null
	}

	return children
}

AuthWrapper.propTypes = {
	children: PropTypes.node.isRequired,
}

const onBeforeAuthentication = async () => {
	/* noop */
}

const AuthGuard = ({ component: Component, fallback: FComponent }) => {
	const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0()

	useEffect(() => {
		const handlePageShow = e => {
			if (e.persisted && !isAuthenticated) {
				window.location.reload()
			}
		}
		window.addEventListener('pageshow', handlePageShow)
		return () => window.removeEventListener('pageshow', handlePageShow)
	}, [isAuthenticated])

	useEffect(() => {
		// If 'nu' param is present and user is still authenticated, wait for Root to handle logout first
		const params = new URLSearchParams(window.location.search)
		if (params.has('nu') && isAuthenticated) {
			return
		}
		if (isLoading || isAuthenticated || !!FComponent) {
			return
		}
		params.delete('nu')
		const cleanSearch = params.toString() ? `?${params.toString()}` : ''
		const opts = {
			appState: {
				returnTo: `${window.location.pathname}${cleanSearch}`,
			},
		}
		;(async () => {
			await onBeforeAuthentication()
			await loginWithRedirect(opts)
		})()
	}, [isLoading, isAuthenticated, loginWithRedirect, FComponent])

	if (!isAuthenticated) return FComponent ? <FComponent /> : <Loader />

	return (
		<AuthWrapper>
			<Component />
		</AuthWrapper>
	)
}

AuthGuard.propTypes = {
	component: PropTypes.elementType.isRequired,
	fallback: PropTypes.elementType,
}

export default AuthGuard
